import type { Attendance, AttendanceStats } from '@shared/types/api'
import {
  countsAsAttended,
  formatRate,
  formatSessionDate,
  formatTimeOfDay,
  statusColor,
  statusLabel,
} from '@shared/utils/attendance'

interface AttendanceHistoryProps {
  items: Attendance[]
  stats?: AttendanceStats
  isLoading?: boolean
  /** Название группы по её id — чтобы показывать имя, а не UUID */
  groupNameById?: (groupId: string) => string | undefined
}

/**
 * Вкладка «Посещения» в карточке спортсмена.
 *
 * Здесь историю только смотрят: отмечают посещаемость на экране занятия,
 * потому что отмечают всю группу разом, а не по одному человеку.
 */
export function AttendanceHistory({
  items,
  stats,
  isLoading = false,
  groupNameById,
}: AttendanceHistoryProps) {
  if (isLoading) {
    return (
      <p className="py-8 text-center text-[15px]" style={{ color: 'var(--text-hint)' }}>
        Загружаем посещения…
      </p>
    )
  }

  if (items.length === 0) {
    return (
      <p className="py-8 text-center text-[15px]" style={{ color: 'var(--text-hint)' }}>
        Пока ни одной отметки
      </p>
    )
  }

  return (
    <div className="flex flex-col">
      {stats && stats.total_sessions > 0 && (
        <p
          className="px-4 py-3 text-[15px]"
          style={{ color: 'var(--text-secondary)' }}
          data-testid="attendance-summary"
        >
          Был на {attendedCount(stats)} из {stats.total_sessions} · {formatRate(stats.attendance_rate)}
        </p>
      )}

      <ul className="flex flex-col" data-testid="attendance-history">
        {items.map((item) => (
          <li
            key={item.id}
            className="flex items-center gap-3 px-4"
            style={{ minHeight: 52, borderBottom: '1px solid var(--separator)' }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor: statusColor(item.status),
              }}
            />

            <span className="text-[15px]" style={{ color: 'var(--text-primary)', minWidth: 110 }}>
              {formatSessionDate(item.date)}
              {formatTimeOfDay(item.training_start_time) &&
                `, ${formatTimeOfDay(item.training_start_time)}`}
            </span>

            <span className="flex-1 text-[15px]" style={{ color: 'var(--text-secondary)' }}>
              {groupNameById?.(item.group_id) ?? ''}
            </span>

            <span
              className="text-[15px]"
              style={{
                color: countsAsAttended(item.status) ? 'var(--text-primary)' : 'var(--text-hint)',
              }}
            >
              {statusLabel(item.status)}
            </span>

            {item.notes && (
              <span className="text-[13px]" style={{ color: 'var(--text-hint)' }}>
                {item.notes}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Сколько занятий засчитано как посещённые: опоздал и ушёл раньше тоже считаются. */
function attendedCount(stats: AttendanceStats): number {
  return stats.present_count + stats.late_count + (stats.left_early_count ?? 0)
}
